import { Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ScorecardRow } from "@/lib/parent/commandMetrics";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import { FocusAreaDrilldown } from "@/components/parent/hub/ParentDrilldownSheet";
import { ChevronRight } from "lucide-react";

const NEXT_STEP_BY_ID: Record<string, string> = {
  money: "More spending and saving decisions will sharpen this signal.",
  credit: "Borrowing and payment lessons will clarify credit readiness.",
  scam: "Scam Defense decisions will show how well pressure and urgency are recognized.",
  decision: "Decision checks reveal whether the learner can explain tradeoffs.",
  module: "Simulator readiness improves as related lessons and checks are completed.",
};

interface Props {
  rows: ScorecardRow[];
  analytics: ParentAnalyticsResult;
}

export default function ReadinessScorecard({ rows, analytics }: Props) {
  const meaningful = rows.filter((r) => r.percent > 0 || r.sampleSize > 0);
  const overallPct = meaningful.length ? Math.round(meaningful.reduce((s, r) => s + r.percent, 0) / meaningful.length) : 0;
  const overallState = stateFromPercent(overallPct, meaningful.length > 0);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-card/85 via-card/65 to-card/45 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)] sm:p-6">
      <div className="pointer-events-none absolute -bottom-28 -right-20 h-56 w-56 rounded-full bg-primary/10 blur-[80px]" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25">
              <Gauge className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold tracking-tight text-foreground sm:text-lg">Readiness</h3>
              <p className="text-[11px] text-muted-foreground">Learning signals across HeirWise modules.</p>
            </div>
          </div>
          <StatePill state={overallState} />
        </div>
        <div className="mt-5 space-y-2.5">
          {rows.map((row) => <ReadinessRow key={row.id} row={row} analytics={analytics} />)}
        </div>
      </div>
    </section>
  );
}

type ReadinessState = "Not started" | "Forming" | "Emerging" | "Strengthening" | "Ready";

function stateFromPercent(p: number, hasSignal: boolean): ReadinessState {
  if (!hasSignal && p === 0) return "Not started";
  if (p >= 75) return "Ready";
  if (p >= 50) return "Strengthening";
  if (p >= 25) return "Emerging";
  return "Forming";
}

function stateForRow(row: ScorecardRow): ReadinessState {
  if (row.signal === "Not Started" || (row.percent === 0 && row.sampleSize === 0)) return "Not started";
  return stateFromPercent(row.percent, true);
}

const STATE_TONE: Record<ReadinessState, { pill: string; bar: string; track: string }> = {
  "Not started": {
    pill: "border-border/40 bg-muted/25 text-muted-foreground",
    bar: "bg-gradient-to-r from-muted-foreground/30 to-muted-foreground/10",
    track: "bg-white/[0.04]",
  },
  Forming: {
    pill: "border-primary/30 bg-primary/10 text-primary",
    bar: "bg-gradient-to-r from-primary/60 via-primary to-primary/70 shadow-[0_0_18px_-2px_hsl(var(--primary)/0.55)]",
    track: "bg-primary/[0.07]",
  },
  Emerging: {
    pill: "border-secondary/30 bg-secondary/10 text-secondary",
    bar: "bg-gradient-to-r from-secondary/60 via-secondary to-primary/80 shadow-[0_0_18px_-2px_hsl(var(--secondary)/0.55)]",
    track: "bg-secondary/[0.07]",
  },
  Strengthening: {
    pill: "border-amber-400/30 bg-amber-500/10 text-amber-200",
    bar: "bg-gradient-to-r from-amber-400/70 via-amber-300 to-emerald-300 shadow-[0_0_18px_-2px_hsl(45_90%_60%/0.55)]",
    track: "bg-amber-500/[0.06]",
  },
  Ready: {
    pill: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
    bar: "bg-gradient-to-r from-emerald-400/80 via-emerald-300 to-emerald-200 shadow-[0_0_22px_-2px_hsl(150_70%_55%/0.6)]",
    track: "bg-emerald-500/[0.06]",
  },
};

function StatePill({ state }: { state: ReadinessState }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-medium", STATE_TONE[state].pill)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {state}
    </span>
  );
}

function ReadinessRow({ row, analytics }: { row: ScorecardRow; analytics: ParentAnalyticsResult }) {
  const state = stateForRow(row);
  const tone = STATE_TONE[state];
  const fillPct = state === "Not started" ? 6 : Math.max(8, Math.min(100, row.percent));
  const specific = NEXT_STEP_BY_ID[row.id] ?? row.nextStep;
  return (
    <FocusAreaDrilldown
      row={row}
      analytics={analytics}
      trigger={
        <button
          type="button"
          className="group w-full rounded-2xl border border-white/[0.05] bg-background/30 p-3 text-left backdrop-blur-md transition hover:border-white/[0.12] hover:bg-background/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/30"
          aria-label={`Open ${row.label} drilldown`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="truncate text-[13px] font-medium text-foreground">{row.label}</p>
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5" />
            </div>
            <StatePill state={state} />
          </div>
          <div className={cn("mt-2 h-2 w-full overflow-hidden rounded-full", tone.track)}>
            <div
              className={cn("h-full rounded-full transition-all duration-700 ease-out", tone.bar)}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
            {state === "Not started" ? "Not enough signal yet — opens after first activity here." : specific}
          </p>
        </button>
      }
    />
  );
}